import * as Sentry from "@sentry/react";
import {
	accessTokenSignal,
	clearProactiveTokenRefresh,
	getShareKey,
	isUnauthenticatedError,
	scheduleProactiveTokenRefresh,
	setupAssetAuth,
} from "@src/auth/auth-utils";
import { auth0Client } from "@src/auth/auth0-provider-with-navigate";
import {
	loginInteractively,
	shouldUseDesktopBrowserLogin,
} from "@src/auth/desktop-browser-login";
import {
	getAuthReturnPathname,
	getAuthReturnTo,
	shouldUseSignupScreenHint,
} from "@src/auth/desktop-browser-login-return";
import { queryClient } from "@src/query-client";
import { buildAuthorizationParams } from "@src/utils/auth-params";
import { captureReferralCode } from "@src/utils/referral-tracker";
import { captureUtmParams } from "@src/utils/utm-tracker";
import { getUserFromAuth0User } from "../user/user-fns";
import { prefetchNavbarTreesForShell } from "./prefetch-navbar-trees";

export async function loader() {
	const shareKey = getShareKey();

	// Capture UTM parameters from URL if present
	const utmParams = captureUtmParams();

	// Capture referral code from URL if present
	const referralCode = captureReferralCode();

	let token: string | null = null;
	let user: Awaited<ReturnType<typeof getUserFromAuth0User>> | null = null;

	try {
		token = await auth0Client.getTokenSilently();
		const auth0User = await auth0Client.getUser();
		user = auth0User ? await getUserFromAuth0User(auth0User) : null;
		const isAuthenticated = !!token;

		if (isAuthenticated) {
			accessTokenSignal.value = token;
			scheduleProactiveTokenRefresh(token);
		}

		try {
			await setupAssetAuth(token, shareKey);
		} catch {
			// Already logged to Sentry inside setupAssetAuth; non-fatal for app load
		}

		if (isAuthenticated) {
			void prefetchNavbarTreesForShell(queryClient);
		}

		return { isAuthenticated, user, skipAuth: false };
	} catch (error) {
		if (isUnauthenticatedError(error)) {
			accessTokenSignal.value = null;
			clearProactiveTokenRefresh();

			if (shareKey === null) {
				// Desktop system-browser login must be user-initiated (Sign in
				// button). Auto-opening the browser after logout/cold start skips
				// the Auth0 UI via SSO and feels broken.
				if (!shouldUseDesktopBrowserLogin()) {
					const pathname = getAuthReturnPathname();
					const authorizationParams: Record<string, string> = {
						...buildAuthorizationParams(utmParams, referralCode),
						...(shouldUseSignupScreenHint(pathname, referralCode)
							? { screen_hint: "signup" }
							: {}),
					};
					await loginInteractively({
						appState: {
							returnTo: getAuthReturnTo(),
						},
						authorizationParams,
					});
				}
			} else {
				try {
					await setupAssetAuth(token, shareKey);
				} catch {
					// Already logged to Sentry inside setupAssetAuth
				}
			}

			return {
				isAuthenticated: false,
				user: null,
				skipAuth: shareKey !== null,
			};
		}

		Sentry.captureException(error);

		throw error;
	}
}
